/**
* @summary - Component that Provides Reset Password functionality
* @autor vengateshwaran k
* @since 2024-05-30
*/
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Box, Button, Grid, Paper, TextField, Typography } from "@mui/material";
import axios from 'axios';
import CryptoJS from "crypto-js";
import { differenceInMinutes, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { ifacSecurityURL } from '../../utility/apiURLS';
import { encryptionKey } from "../../utility/secretKeys";
const ResetPassword = () => {
  const location = useLocation();
  const [token, setToken] = useState('');
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [tokenObject, setTokenObject] = useState({});
  const [encryptedToken, setEncryptedToken] = useState();
  const [isWithinTimeFrame, setIsWithinTimeFrame] = useState(false);
  const [messagePrevent, setMessagePrevent] = useState(false);

  const schema = yup.object().shape({
    password: yup.string().required('Password is required').min(8, 'Password must be at least 8 characters').max(14, 'Password must be at most 14 characters')
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^])[A-Za-z\d@$!%*?&#^]+$/, 'Password must be alphanumeric with special characters'),
    confirmPassword: yup.string().oneOf([yup.ref('password'), null], 'Confirm Password must match')
  });
  const { register, handleSubmit, formState: { errors }, trigger } = useForm({
    resolver: yupResolver(schema)
  });
  const decrypt = async (key, content) => {
    try {
      // Ensure the key is exactly 32 bytes (256 bits) for AES-256
      let keyBytes = CryptoJS.enc.Utf8.parse(key);
      let keyWords = keyBytes.words.slice(0, 8);  // AES-256 uses 8 words (32 bytes)
      let truncatedKey = CryptoJS.lib.WordArray.create(keyWords, 32);

      // Convert base64-encoded content back to bytes
      let encryptedBytes = CryptoJS.enc.Base64.parse(content);

      // Extract IV from the beginning of the encrypted data
      let iv = CryptoJS.lib.WordArray.create(encryptedBytes.words.slice(0, 4), 16);

      // Extract the actual encrypted data
      let encryptedData = CryptoJS.lib.WordArray.create(encryptedBytes.words.slice(4), encryptedBytes.sigBytes - 16);

      // Decrypt the data
      let decrypted = CryptoJS.AES.decrypt(
        { ciphertext: encryptedData },
        truncatedKey,
        {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7
        }
      );

      return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      // Handle exceptions related to decryption
      console.error('Decryption error:', error.message);
      throw error;
    }
  }

  useEffect(() => {
    const getToken = async () => {
      try{
      const encodedToken = location.search.replace('token=', '');
      const cleanToken = encodedToken.startsWith('?') ? encodedToken.replace('?', '') : encodedToken;
      setEncryptedToken(cleanToken);
      const decryptedContent = await decrypt(encryptionKey, cleanToken);
      setTokenObject(JSON.parse(decryptedContent));
      if (decryptedContent) {

        const utcTime = parseISO(JSON.parse(decryptedContent)?.TokenDate);
        // Convert the provided UTC time to IST (UTC+5:30)
        const istTime = toZonedTime(utcTime, 'Asia/Kolkata');
        // Get the current IST time
        const currentISTTime = toZonedTime(new Date(), 'Asia/Kolkata');
        // Calculate the difference in minutes
        const differenceInMinutesValue = differenceInMinutes(currentISTTime, istTime);
        // Check if the difference is within 20 minutes
        const isWithinTwentyFiveMinutes = currentISTTime > istTime;
        setIsWithinTimeFrame(!isWithinTwentyFiveMinutes);
        setMessagePrevent(isWithinTwentyFiveMinutes);
      }
    }catch(error){
      console.error(error.message);
    }
    }
    getToken();
  }, []);

  const onSubmit = async (data) => {
    try {

      if (isWithinTimeFrame) {
        const response = await axios.post(`${ifacSecurityURL}/api/account/reset-password`, { "token": encryptedToken, "password": data.password, "tokenDate": tokenObject?.TokenDate, "email": tokenObject?.Email, "userId": tokenObject?.UserId, "JwtToken": "string" });
        if (response.status === 200) {
          setSuccessMessage('Password reset successfully. Redirecting to login...');
          setTimeout(() => {
            navigate('/login');
          }, 5000);
        }
      }
      else {
        setErrorMessage('You must Change Your password within 30 minutes after mail validation');
      }
    } catch (error) {
      setErrorMessage('An error occurred. Please try again later.');
    }
  };

  return (<>
    {isWithinTimeFrame ? <Paper sx={{ margin: '20px' }}>
      <Box mt={12} display="flex" flexDirection="column" alignItems="flex-start" >
        <Grid container style={{ margin: '20px' }}>
          <Grid item xs={12} sm={12}>
            <Typography component="h1" variant="h5">
              Reset Password
            </Typography>
          </Grid>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%', marginTop: '1rem' }}>
            <Grid container sx={{ display: "flex", justifyContent: "center" }}>
              <Grid container >
                <Grid item xs={12} sm={4} style={{ marginTop: '20px' }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Typography variant='h6' color={'text.primary'} component={'label'} >New Password<Typography component={"span"} color={"error"}>
                    *
                  </Typography>    </Typography>
                </Grid>
                <Grid item xs={12} sm={2} sx={{ marginLeft: '20px' }}>
                  <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id="password"
                    placeholder="Enter New Password"
                    inputProps={{ minlength: 8, maxLength: 14 }}
                    type="password"
                    name="password"
                    autoComplete="new-password"
                    {...register("password")}
                    error={!!errors.password}
                    helperText={errors.password ? errors.password.message : ''}
                  /></Grid></Grid>
              <Grid container >
                <Grid item xs={6} sm={4} style={{ marginTop: '20px' }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Typography variant='h6' color={'text.primary'} component={'label'} >Confirm Password<Typography component={"span"} color={"error"}>
                    *
                  </Typography> </Typography>
                </Grid>
                <Grid item xs={12} sm={2} sx={{ marginLeft: '20px' }}>
                  <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id="confirmPassword"
                    placeholder="Enter Confirm Password"
                    inputProps={{ minlength: 8, maxLength: 14 }}
                    type="password"
                    name="confirmPassword"
                    autoComplete="new-password"
                    {...register("confirmPassword")}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword ? errors.confirmPassword.message : ''}
                  /></Grid></Grid>
              <Grid container>
                <Grid item xs={6} sm={12}>
                  <Typography sx={{ margin: 2 }} >
                    1. Password should be mix of alphabets with min one Upper case, numerals and special characters (@$!%*?&#^])[A-Za-z\d) without any space between.
                  </Typography>

                  <Typography sx={{ margin: 2 }} >
                    2. The Password length should be between 8 to 14 characters.
                  </Typography>
                  <Typography sx={{ margin: 2 }} >
                    3. New Password Cannot be same the previous Password.
                  </Typography>
                </Grid>

              </Grid>
              <Grid container sx={{ display: "flex", justifyContent: "flex-end", margin: '20px' }}>
                <Grid item xs={12} sm={2} style={{ margin: '20px' }}  >
                  {errorMessage && (
                    <Alert severity="error" style={{ marginTop: '1rem' }}>{errorMessage}</Alert>
                  )}
                  {successMessage ? (
                    <Alert severity="success" style={{ marginTop: '1rem' }}>{successMessage}</Alert>
                  ) : <Button type="submit" fullWidth variant="contained" color="primary" style={{ marginTop: '1rem' }}>
                    Reset
                  </Button>}
                </Grid>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Box>
    </Paper> :location.search && messagePrevent ? <p>Reset password link has been expired.</p>:""}</>

  );
};

export default ResetPassword;
